import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import cn from 'classnames';
import './mobile-drawer.scss';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  onLogout: () => void;
}

const NAV_LINKS = [
  { label: 'My Trips', href: '/app/trips' },
  { label: 'Public Trips', href: '/app/public-trips' },
  { label: 'Shared With Me', href: '/app/invitations' },
  { label: 'Settings', href: '/app/settings' }
];

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  open,
  onClose,
  onLogout
}) => {
  const location = useLocation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus close button when opened (accessibility)
  useEffect(() => {
    if (open) {
      closeButtonRef.current?.focus();
    }
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <div
      className={cn('mobile-drawer-overlay', { open })}
      onClick={onClose}
      role='dialog'
      aria-modal='true'
      aria-label='Navigation menu'
    >
      <div
        className={cn('mobile-drawer', { open })}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='drawer-header'>
          <span className='drawer-brand'>
            Travel <span className='drawer-brand-accent'>Hearts</span>
          </span>
          <button
            ref={closeButtonRef}
            className='drawer-close-btn'
            onClick={onClose}
            aria-label='Close navigation menu'
          >
            <X size={20} />
          </button>
        </div>

        <nav aria-label='Main navigation'>
          {NAV_LINKS.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              className={cn({ active: location.pathname.startsWith(href) })}
              onClick={onClose}
            >
              {label}
            </a>
          ))}
        </nav>

        <div className='drawer-footer'>
          <button onClick={onLogout}>Log out</button>
        </div>
      </div>
    </div>
  );
};

export default MobileDrawer;
